// ** React Imports
import { useEffect, useState } from 'react'

import { client } from 'src/utils/axios-client';
import { LeadScreenStore, LeadNote, TaskList, NewTaskStore, CompletedTaskStore, DashboardViewStore } from 'src/stores/LeadStore';


// ** MUI Imports
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import Avatar from '@mui/material/Avatar'
import { styled } from '@mui/material/styles'
import TimelineDot from '@mui/lab/TimelineDot'
import TimelineItem from '@mui/lab/TimelineItem'
import CardHeader from '@mui/material/CardHeader'
import Typography from '@mui/material/Typography'
import CardContent from '@mui/material/CardContent'
import TimelineContent from '@mui/lab/TimelineContent'
import TimelineSeparator from '@mui/lab/TimelineSeparator'
import TimelineConnector from '@mui/lab/TimelineConnector'
import MuiTimeline, { TimelineProps } from '@mui/lab/Timeline'
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import PerfectScrollbar from 'react-perfect-scrollbar'
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import SaveIcon from '@mui/icons-material/Save';
import Divider from '@mui/material/Divider'
import Tooltip from '@mui/material/Tooltip';
import TaskIcon from '@mui/icons-material/Task';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import { Controller } from 'react-hook-form';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { SelectOption } from 'src/types/common';
import MenuItem from '@mui/material/MenuItem';
import { leadEndpoints, optionsEndpoints } from '../../configs/endpoints';
import { ProblemDetails } from '../../types/ApiErrorResponse';
import CustomDatePickerNoError from '../common/CustomDatePickerNoError';
import TaskItem from '../leads/view/TaskItem';
import StandardSkeleton from '../common/StandardSkeleton';
import IconButton from '@mui/material/IconButton';
import ArrowBackTwoToneIcon from '@mui/icons-material/ArrowBackTwoTone';
import RefreshTwoToneIcon from '@mui/icons-material/RefreshTwoTone';
import TaskDetails from './TaskDetails';
import GridSkeleton from '../common/GridSkeleton';

// Styled Timeline component
const Timeline = styled(MuiTimeline)<TimelineProps>(({ theme }) => ({
    margin: 0,
    padding: 0,
    marginLeft: theme.spacing(0.75),
    '& .MuiTimelineItem-root': {
        '&:before': {
            display: 'none'
        },
        '&:last-child': {
            minHeight: 60
        }
    }
}))

type Props = {
    userId: string,
    title: string
}

const TasksToday = ({ userId, title }: Props) => {
    const [records, setRecords] = useState([] as TaskList[]);
    const task = NewTaskStore.useState(s => s);
    const hasChanged = CompletedTaskStore.useState(s => s.hasChanged);

    const [hasLoaded, setHasLoaded] = useState(false);
    const [showAddTask, setShowAddTask] = useState(false);
    const [taskValue, setTaskValue] = useState('');
    const [taskAdded, setTaskAdded] = useState(false);

    const [selectedOwner, setSelectedOwner] = useState<string>('')
    const [options, setOptions] = useState([] as SelectOption[]);
    const [startDate, setStartDate] = useState<string>('')
    const [reloadRecords, setReloadRecords] = useState<boolean>(false)

    useEffect(() => {
        const fetchTasks = async () => {
            setHasLoaded(false);
            const response = await client.get(leadEndpoints.gettodaytasks, { params: { userId } });
            setRecords(response.data);
            setHasLoaded(true);
        }

        if (task) fetchTasks();
        if (!task) setRecords([]);
    }, [taskAdded, hasChanged, reloadRecords])

    useEffect(() => {
        const load = async () => {
            if (!task) return;
            var response = await client.get(optionsEndpoints.ownerEndpoint);
            setOptions(response.data);
        };

        load();
    }, [])

    const onAddNewTask = () => {
        setTaskValue('');
        setSelectedOwner('');
        setShowAddTask(true);
        setTaskAdded(false);
    };

    const onCancel = () => {
        setTaskValue('');
        setShowAddTask(false);
    };



    const onSubmit = async () => {
        if (task.ownerId == "") return;
        if (task.details == "") return;
        

        await client.post(leadEndpoints.createnewtask, task)
            .then(async (response) => {

                setShowAddTask(false);
                setTaskAdded(true);

            }).catch((err: ProblemDetails) => {
                if (err.errors) {
                    for (const [key, value] of Object.entries(err.errors)) {
                        var errorMessage = '';

                        value.forEach((message) => {
                            message += message + '\\'
                        });
                    }
                }
            });
    }

    const markComplete = async (taskid: string) => {
        await client.post(leadEndpoints.marktaskascomplete.replace('{taskid}', taskid))
            .then(async (response) => {

                setShowAddTask(false);
                setTaskAdded(true);

            }).catch((err: ProblemDetails) => {
                if (err.errors) {
                    for (const [key, value] of Object.entries(err.errors)) {
                        var errorMessage = '';

                        value.forEach((message) => {
                            message += message + '\\'
                        });
                    }
                }
            });
    }

    const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setTaskValue(event.target.value);
        NewTaskStore.update(s => { s.details = event.target.value as string })
    };

    const handleOwnerChange = (event: SelectChangeEvent) => {
        setSelectedOwner(event.target.value as string);
        NewTaskStore.update(s => { s.ownerId = event.target.value as string })
    };

    const goBack = () => {
        DashboardViewStore.update(s => { s.activityDate = '' });
        DashboardViewStore.update(s => { s.showTodoList = true });
        DashboardViewStore.update(s => { s.showActivityWorkList = false });
        DashboardViewStore.update(s => { s.showTaskList = false });
        DashboardViewStore.update(s => { s.showInboundCallList = false });
        DashboardViewStore.update(s => { s.showDefault = true });
        DashboardViewStore.update(s => { s.showDetail = false });
    };

    const reload = () => {
        if (reloadRecords) {
            setReloadRecords(false);
        }
        else {
            setReloadRecords(true);
        }
    };


    if (hasLoaded) {
        return <Card sx={{ width: '100%' }}>
            <Box sx={{ p: 5, pb: 3, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ mr: 4, mb: 2 }}>
                    <Tooltip title="Go Back" >
                        <IconButton aria-label="Go Back to Dashboard" onClick={() => goBack()} >
                            <ArrowBackTwoToneIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
                <Box sx={{ mr: 4, mb: 2 }}>
                    <Typography sx={{ fontSize: 20, color: 'CornflowerBlue' }} gutterBottom>
                        {title}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
                    <Tooltip title="Refresh" >
                        <IconButton aria-label="Next activity" onClick={() => reload()} >
                            <RefreshTwoToneIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>
                <Timeline>
                    <PerfectScrollbar options={{ wheelPropagation: false }}>
                        <Box sx={{ height: 750 }} >
                            {records.length == 0 &&
                                <Typography sx={{ color: 'DarkGrey', ml: 3 }}>
                                    No Open Tasks
                                </Typography>
                            }
                            {records.map(record => (
                                <TimelineItem key={record.id} data-testid="task">
                                    <TimelineSeparator>
                                        <TimelineDot sx={{ backgroundColor: 'green' }} />
                                        <TimelineConnector />
                                    </TimelineSeparator>
                                    <TimelineContent>
                                        <TaskDetails record={record} />
                                    </TimelineContent>
                                </TimelineItem>
                            ))}
                        </Box>
                    </PerfectScrollbar>
                </Timeline>
            </Card>
        
    }
    else {
        return <GridSkeleton />
    }

}

export { TasksToday }

export default TasksToday;